import React, { useContext, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import moment from "moment";
import { UserContext } from "../../hooks/userContext";
import { PharmacyContext } from "../../hooks/pharmacyContext";
import { useQueryWantedItems } from "../../queries/wantedItem/hooks/useQueryWantedItems";
import { useLazyQueryOrders } from "../../queries/order/hooks/useLazyQueryOrders";
import { useQueryHistory } from "../../queries/order/hooks/useQueryHistory";
import { currencyFormat } from "../../components/utils/utils";
import { BODY_FONT, INK_SOFT, PAGE_BG } from "./dashboardTokens";
import { pageTitleSx } from "../../constants/designTokens";
import { DashboardSearchBar } from "./components/DashboardSearchBar";
import { KpiCards } from "./components/KpiCards";
import { RecentOrdersTable, DashboardOrderRow } from "./components/RecentOrdersTable";
import { WishlistQuickView } from "./components/WishlistQuickView";
import { AccountHealthCard } from "./components/AccountHealthCard";

const monthStart = moment().startOf("month").toISOString();

const uniquePendingOrderCount = (items: any[] | undefined): number => {
  if (!items?.length) return 0;
  const seen = new Set<string>();
  items.forEach((item) => {
    if (item?.orderNumber != null) seen.add(String(item.orderNumber));
  });
  return seen.size;
};

const toDashboardRows = (
  orders: any[] | undefined,
  type: "Buy" | "Sell"
): DashboardOrderRow[] => {
  if (!orders?.length) return [];

  const byOrder = new Map<string, DashboardOrderRow>();

  orders.forEach((order) => {
    const key = String(order.orderNumber);
    const amount = Number(order.totalAmount) || 0;
    const existing = byOrder.get(key);

    if (!existing) {
      byOrder.set(key, {
        id: order.id,
        orderNumber: key,
        type,
        createdAt: order.createdAt,
        amount,
        status: order.status,
        shippingStatus: order.shippingStatus,
        paymentStatus: order.paymentStatus,
      });
      return;
    }

    existing.amount += amount;
    // Prefer the earliest shipping progress for a grouped view
    if ((order.shippingStatus ?? 0) < (existing.shippingStatus ?? 0)) {
      existing.shippingStatus = order.shippingStatus;
      existing.paymentStatus = order.paymentStatus;
      existing.status = order.status;
    }
  });

  return Array.from(byOrder.values());
};

export const Dashboard: React.FunctionComponent = () => {
  const user: any = useContext(UserContext);
  const pharmacyUser: any = useContext(PharmacyContext);
  const userId = +user?.id;
  const pharmacy = pharmacyUser?.Pharmacy;

  const { data: wishlistData, loading: wishlistLoading } = useQueryWantedItems({
    variables: {
      where: { buyerUserId: userId },
    },
    fetchPolicy: "cache-and-network",
    skip: !userId,
  });

  const [getPendingOrders, { data: pendingData, loading: pendingLoading }] = useLazyQueryOrders({
    variables: {
      where: {
        AND: [
          { sellerUserId: { equals: userId } },
          { isConfirmed: { equals: false } },
          { status: { equals: 1 } },
        ],
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const [getBuyHistory, { data: buyData, loading: buyLoading }] = useQueryHistory({
    fetchPolicy: "cache-and-network",
  });

  const [getSellHistory, { data: sellData, loading: sellLoading }] = useQueryHistory({
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!userId) return;

    getPendingOrders();

    getBuyHistory({
      variables: {
        where: {
          AND: [
            { buyerUserId: { equals: userId } },
            { createdAt: { gte: monthStart } },
          ],
        },
        orderBy: [{ createdAt: "desc" }],
      },
    });

    getSellHistory({
      variables: {
        where: {
          AND: [
            { sellerUserId: { equals: userId } },
            { createdAt: { gte: monthStart } },
          ],
        },
        orderBy: [{ createdAt: "desc" }],
      },
    });
  }, [userId, getPendingOrders, getBuyHistory, getSellHistory]);

  const wishlistItems = useMemo(
    () => wishlistData?.findManyWantedItem || [],
    [wishlistData?.findManyWantedItem]
  );

  const pendingOrdersCount = uniquePendingOrderCount(pendingData?.findManyOrder);

  const buyOrders = buyData?.findManyOrder || [];
  const sellOrders = sellData?.findManyOrder || [];

  const monthlyVolume = useMemo(() => {
    const sumActive = (orders: any[]) =>
      orders
        .filter((o) => o.status === 1)
        .reduce((acc, o) => acc + (Number(o.totalAmount) || 0), 0);

    return sumActive(buyOrders) + sumActive(sellOrders);
  }, [buyOrders, sellOrders]);

  const recentRows = useMemo(() => {
    const combined = [
      ...toDashboardRows(buyOrders, "Buy"),
      ...toDashboardRows(sellOrders, "Sell"),
    ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return combined.slice(0, 8);
  }, [buyOrders, sellOrders]);

  const bankAccounts = pharmacy?.BankAccount || [];
  const bankingVerified = bankAccounts.some(
    (item: any) =>
      (item.object === "payment_method" || item.object === "card") &&
      (item.status === "VERIFIED" || item.status === "APPROVED")
  );
  const pharmacyStatusKey = pharmacy?.PharmacyStatus?.Status?.key || "";
  const pharmacyVerified = pharmacyStatusKey === "APPROVED";
  const pharmacyStatusLabel =
    pharmacy?.PharmacyStatus?.Status?.name ||
    (pharmacyStatusKey
      ? pharmacyStatusKey.charAt(0) + pharmacyStatusKey.slice(1).toLowerCase()
      : "Unknown");

  const complianceLabel = pharmacyVerified && bankingVerified ? "Verified" : "Action needed";
  const complianceHint =
    pharmacyVerified && bankingVerified
      ? "Pharmacy & banking verified"
      : !pharmacyVerified
        ? "Complete pharmacy verification"
        : "Add a verified payment method";

  const metricsLoading = wishlistLoading || pendingLoading || buyLoading || sellLoading;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        pb: 3,
        minHeight: "100%",
        bgcolor: PAGE_BG,
      }}
    >
      <Box>
        <Typography component="h1" sx={pageTitleSx}>
          Dashboard
        </Typography>
        <Typography sx={{ fontFamily: BODY_FONT, fontSize: 14, color: INK_SOFT, mt: 0.5 }}>
          Marketplace overview, orders, and account health
        </Typography>
      </Box>

      <DashboardSearchBar />

      <KpiCards
        wishlistCount={wishlistItems.length}
        pendingOrdersCount={pendingOrdersCount}
        monthlyVolumeLabel={currencyFormat(monthlyVolume)}
        complianceLabel={complianceLabel}
        complianceHint={complianceHint}
        loading={metricsLoading && recentRows.length === 0 && wishlistItems.length === 0}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.7fr) minmax(280px, 1fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <RecentOrdersTable rows={recentRows} loading={buyLoading || sellLoading} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <WishlistQuickView items={wishlistItems} loading={wishlistLoading} />
          <AccountHealthCard
            pharmacyVerified={pharmacyVerified}
            pharmacyStatusLabel={pharmacyStatusLabel}
            bankingVerified={bankingVerified}
            bankingLabel={bankingVerified ? "Payment method verified" : "Verification required"}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
